/**
 * Dynamic Terminology Mapping
 * Maps UI labels based on projectType (Software, Construction, Finance)
 */

type ProjectType = 'Software' | 'Construction' | 'Finance' | 'Other';

interface TerminologyMap {
  largeGoal: string;
  workflow: string;
  iterationCycle: string;
  lookahead: string;
  dailyAction: string;
  errorIssue: string;
}

const TERMINOLOGY_MAPS: Record<ProjectType, TerminologyMap> = {
  'Software': {
    largeGoal: 'Epic',
    workflow: 'Sprint',
    iterationCycle: 'Sprint',
    lookahead: 'Look-Ahead',
    dailyAction: 'Story',
    errorIssue: 'Bug'
  },
  'Construction': {
    largeGoal: 'Phase / Work Package',
    workflow: 'Look-Ahead',
    iterationCycle: 'Look-Ahead',
    lookahead: 'Look-Ahead',
    dailyAction: 'Activity / Line Item',
    errorIssue: 'Punch List / Defect'
  },
  'Finance': {
    largeGoal: 'Initiative / Workstream',
    workflow: 'Reporting Cycle',
    iterationCycle: 'Reporting Cycle',
    lookahead: 'Reporting Cycle',
    dailyAction: 'Action Item',
    errorIssue: 'Variance / Exception'
  },
  'Other': {
    largeGoal: 'Large Goal',
    workflow: 'Work Cycle',
    iterationCycle: 'Work Cycle',
    lookahead: 'Look-Ahead',
    dailyAction: 'Task',
    errorIssue: 'Issue'
  }
};

/**
 * Get terminology map for a project type
 */
export function getTerminologyMap(projectType: ProjectType = 'Software'): TerminologyMap {
  return TERMINOLOGY_MAPS[projectType] || TERMINOLOGY_MAPS['Software'];
}

/**
 * Get specific term by key and project type
 */
export function getTerm(key: keyof TerminologyMap, projectType: ProjectType = 'Software'): string {
  const map = getTerminologyMap(projectType);
  return map[key];
}

/**
 * Label transformations for UI display
 */
export function getEpicLabel(projectType: ProjectType = 'Software'): string {
  return getTerm('largeGoal', projectType);
}

export function getSprintLabel(projectType: ProjectType = 'Software'): string {
  return getTerm('workflow', projectType);
}

export function getTaskLabel(projectType: ProjectType = 'Software'): string {
  return getTerm('dailyAction', projectType);
}

export function getBugLabel(projectType: ProjectType = 'Software'): string {
  return getTerm('errorIssue', projectType);
}

/**
 * Format label for display in UI (with count if provided)
 */
export function formatLabel(label: string, count?: number): string {
  if (count !== undefined) {
    if (count === 1) {
      return `${label} (${count})`;
    }

    const plural = label.endsWith('y') ? `${label.slice(0, -1)}ies` : `${label}s`;
    return `${plural} (${count})`;
  }
  return label;
}

/**
 * Get all terminology for a project type (for sidebar/menu rendering)
 */
export function getAllTerminology(projectType: ProjectType = 'Software'): Record<string, string> {
  const map = getTerminologyMap(projectType);
  return {
    'Epic': getTerm('largeGoal', projectType),
    'Sprint': getTerm('workflow', projectType),
    'Task': getTerm('dailyAction', projectType),
    'Bug': getTerm('errorIssue', projectType),
    'LookAhead': getTerm('lookahead', projectType)
  };
}

/**
 * Check if project type is construction (hard-coded for template checks)
 */
export function isConstructionProject(projectType?: ProjectType): boolean {
  return projectType === 'Construction';
}

/**
 * Check if project type is software
 */
export function isSoftwareProject(projectType?: ProjectType): boolean {
  return projectType === 'Software';
}

/**
 * Check if project type is finance
 */
export function isFinanceProject(projectType?: ProjectType): boolean {
  return projectType === 'Finance';
}
